import React, {useEffect, useState} from "react";
import { GatsbyImage, getImage, getSrc } from "gatsby-plugin-image";
import RichSnippets from "cube-components/blog/PostRichSnippets";
import { logoUrl, origin } from "components/index/Config";
import { trimStart } from "lodash";
import "../../static/prism";
import SocialShare from "cube-components/blog/SocialShare";
import TagsView from "cube-components/blog/TagsView";
import Seo from "cube-components/Seo";
import PageLayout from "cube-components/PageLayout";
import Heading from "cube-components/ui/Heading";
import { graphql } from "gatsby";
import MagentoConsultingBanner from "../cube-components/cta/MagentoConsultingBanner";

const getAbsoluteUrl = slug => origin + trimStart(slug, "/");

const Post = ({ data }) => {
  const { markdownRemark } = data;
  const { frontmatter } = markdownRemark;
  const { html } = markdownRemark;
  const featuredImage = getImage(frontmatter.featuredImage);
  const authorImage = getImage(frontmatter.authorImage);
  const {
    tags,
    title,
    author,
    description,
    updated,
    slug,
    featuredImageAuthor,
    featuredImageAuthorHtml
  } = frontmatter;

  useEffect(() => {
    Prism.highlightAll();
  }, []);

  const renderFeaturedImageAuthor = () => {
    if (featuredImageAuthor) {
      return `Picture by ${featuredImageAuthor}`;
    }
    if (featuredImageAuthorHtml) {
      return (
        <span dangerouslySetInnerHTML={{ __html: featuredImageAuthorHtml }} />
      );
    }

    return "";
  };

  const [consultingVisible, setConsultingVisible] = useState(true);

  return (
    <PageLayout>
      <section className="relative border-t border-lucid-800">
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="pt-32 md:pt-40">
            <div className="max-w-3xl mx-auto text-left">
              <Heading title={title}
                       subtitle={description}
              />
              <div className="mb-10
              border-t-4
              border-lucid-800
              rounded-2xl
              pt-10 pb-5
              bg-gradient-to-b from-lucid-800 to-lucid-transparent
              shadow-inner shadow-lucid-900
              "
              >
                <GatsbyImage
                  className="text-lucid-300 shadow-md rounded-b-2xl"
                  image={featuredImage}
                  alt={`Picture for ${title}`}
                />
                <div className="mt-3 text-center text-sm text-lucid-500">{renderFeaturedImageAuthor()}</div>
              </div>
              <div className="flex justify-between items-center
              text-lucid-300
              md:text-xl
              p-2 mt-5 mb-10
              md:p-3
              border-2
              border-lucid-800
              shadow-md
              rounded-full
              bg-gradient-to-b from-lucid-900 to-lucid-800
              "
              >
                <div className="flex items-center gap-2">
                  <GatsbyImage
                    className="w-16 md:w-32 rounded-full"
                    image={authorImage}
                    alt="Author's photo"
                  />
                  <span className="font-bold">{author}</span>
                </div>
                <div className="text-lucid-400 md:text-lg pr-8"><span className="hidden md:contents">Last updated: </span>{updated}</div>
              </div>
              <div
                dangerouslySetInnerHTML={{ __html: html }}
                className="blogpost-content mb-20"
              />

              {consultingVisible && (
                <MagentoConsultingBanner onClose={() => setConsultingVisible(false)}/>
              )}

              <SocialShare url={getAbsoluteUrl(slug)} />
              <TagsView tags={tags} />
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};
export default Post;

export const Head = ({ data }) => {
  const { markdownRemark } = data;
  const { frontmatter } = markdownRemark;
  const richSnippetsImageSrc = getSrc(frontmatter.featuredImage);
  const richSnippetsImageUrl = getAbsoluteUrl(richSnippetsImageSrc);

  const {
    title,
    author,
    description,
    date,
    updated,
    slug,
  } = frontmatter;

  const metaTitle = title + ' | Lucid Modules';

  const meta = {
    title: metaTitle,
    description,
    url: getAbsoluteUrl(slug),
    image: richSnippetsImageUrl
  };

  return (
    <Seo
      lang="en"
      meta={meta}
      richSnippets={RichSnippets({
        postUrl: getAbsoluteUrl(slug),
        description: description,
        author: author,
        createdAt: date,
        updatedAt: updated,
        imageUrl: richSnippetsImageUrl,
        organizationName: "Lucid Modules",
        organizationLogoUrl: logoUrl
      })}
    />
  );
};

export const pageQuery = graphql`
    query BlogPostBySlug(
        $id: String!
    ) {
        markdownRemark(id: { eq: $id }) {
            frontmatter {
                slug
                title
                tags
                description
                date(formatString: "MMMM DD, YYYY")
                updated(formatString: "MMMM DD, YYYY")
                author
                authorImage {
                    childImageSharp {
                        gatsbyImageData
                    }
                }
                featuredImage {
                    childImageSharp {
                        gatsbyImageData
                    }
                }
                featuredImageAuthor
                featuredImageAuthorHtml
            }
            html
        }
    }
`;
